import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";

import CoAppDataGridListCell from "../../../global/components/datagrid/CoAppDataGridListCell";
import CoAppDataGridNameCell from "../../../global/components/datagrid/CoAppDataGridNameCell";
import RowActionIcons from "../../../global/components/RowActionIcons";
import { CondensedChipList } from "../../../global/components/TagChip";

// Convert backend permission constants like "MANAGE_ORG_FORCELOGIN" to a
// readable "Manage Org Forcelogin" form when no friendly name is provided.
function humanizePermission(name: string): string {
	if (!name) return "";
	// If it's already mixed case (likely a friendly name), keep as-is.
	if (name !== name.toUpperCase()) return name;
	return name
		.toLowerCase()
		.split(/[_\s-]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

// `actions` last → three-dot menu on the right edge of each row.
export const roleColumns = ["name", "users", "permissions", "actions"];
export const ssoAndScimEnabledRoleColumns = ["name", "users", "permissions", "idpgroups", "actions"];

interface Permission {
	friendlyName?: string;
	name?: string;
	[key: string]: unknown;
}

export interface RoleRow {
	id: string | number;
	name: string;
	users?: string[];
	permissions?: Permission[];
	idpgroups?: string[];
	[key: string]: unknown;
}

export const getRoleManagementColumns = (
	openResourceEditPage: (params: GridRenderCellParams<RoleRow>, navIndex: number, resourceName: string) => void,
	duplicateRole: (roleId: RoleRow["id"]) => void,
	handleOpenRoleDeletionDialog: (role: RoleRow) => void,
	organizationSsoAndScim: boolean,
	identityProviderName: string | null,
	navigationItemReferences: { roleNavItemIndex: number } & Record<string, number>,
): GridColDef<RoleRow>[] => [
	{
		field: "name",
		headerName: "Name",
		sortable: true,
		editable: false,
		flex: .5,
		renderCell: (params: GridRenderCellParams<RoleRow>) => (
			<CoAppDataGridNameCell item={params.row} />
		),
	},
	{
		field: "users",
		headerName: "Users",
		editable: false,
		sortable: false,
		flex: .5,
		renderCell: (params: GridRenderCellParams<RoleRow, string[] | undefined>) => (
			<CoAppDataGridListCell items={params.value} resource="Users" isUser={true} width={225} />
		)
	},
	{
		field: "permissions",
		headerName: "Permissions",
		editable: false,
		sortable: false,
		// Bumped from 1.25 → 2.75 so multi-row tag wrapping has horizontal
		// room for long permission names like "Manage Organization
		// Killswitch" without clipping mid-word.
		flex: 2.75,
		minWidth: 480,
		renderCell: (params: GridRenderCellParams<RoleRow, Permission[] | undefined>) => {
			const items = (params.value ?? [])
				.map((permission) => humanizePermission(permission.friendlyName ?? permission.name ?? ""))
				.filter(Boolean);
			return (
				<div
							style={{
								display: "flex",
								alignItems: "center",
								height: "100%",
								width: "100%",
								padding: "6px 0",
								maxWidth: "100%",
								overflow: "hidden",
							}}
						>
							<CondensedChipList labels={items} max={3} emptyLabel="No permissions" />
						</div>
			);
		}
	},
	...(organizationSsoAndScim
		? [
			{
				field: "idpgroups",
				headerName: identityProviderName ? `${identityProviderName} Groups` : "Groups",
				editable: false,
				sortable: false,
				flex: 1.25,
				renderCell: (params: GridRenderCellParams<RoleRow, string[] | undefined>) => (
					<CoAppDataGridListCell items={params.value} resource={identityProviderName ? `${identityProviderName} Groups` : "Groups"} />
				),
			},
		]
		: []),
	{
		field: "actions",
		type: "actions",
		width: 130,
		sortable: false,
		filterable: false,
		hideable: false,
		headerName: "",
		align: "right",
		headerAlign: "right",
		renderCell: (params: GridRenderCellParams<RoleRow>) => (
			<RowActionIcons
						onEdit={() => openResourceEditPage(params, navigationItemReferences.roleNavItemIndex, "Roles")}
						onDuplicate={() => duplicateRole(params.row.id)}
						onDelete={() => handleOpenRoleDeletionDialog(params.row)}
					/>
		),
	},
];
